import React from "react";
import PropTypes from "prop-types";
import { TableWrapperDefault, PainTable, ScrollableTableContainer,ScrollableTables, SafetyTable, TableWrapperDosing, DosingTable, AccessDosesTable, AccessReimbursementTable,MultiModalProtocolTable, MultiModalProtocolTable2, PediatricSurgeryTable } from "./table.css";
import Text from "@components/RTE/Text";
import InfoIcon from "@images/icons/info-icon.svg";
import ErrorComponent from "@components/Error";

/**
 * Renders a Contentful rich text document that may contain tables, text, or both
 * 
 * @param {Object} props - Component props
 * @param {Object} props.content - Content data from Contentful
 * @param {string} props.className - CSS class to apply
 * @param {string} props.tableType - Type of table (Pain, Safety, Dosing, etc.) for any tables in the content
 * @returns {React.ReactElement} Rendered content
 */
const Table = ({ content, className, tableType }) => {
    if (!content) return null;
    
    // Parse the content if needed
    let parsedContent;
    try {
        // Case 1: Direct JSON string (e.g., from an API response or stored JSON)
        if (typeof content === 'string') {
            parsedContent = JSON.parse(content);
        } 
        // Case 2: Contentful table object with nested table.raw property
        // This is the common format when receiving data directly from Contentful
        else if (content && content.table && content.table.raw) {
            parsedContent = JSON.parse(content.table.raw);
        } 
        // Case 3: Contentful rich text object with raw property
        // This happens when the table is embedded in a rich text field
        else if (content && content.raw) {
            parsedContent = JSON.parse(content.raw);
        } 
        // Case 4: Already parsed object
        // Either pre-processed or passed from another component
        else {
            parsedContent = content;
        }
    } catch (error) {
        console.error("Error parsing content:", error);
        return <ErrorComponent message="Error parsing content" />;
    }

    // Check if this is a single table or a document potentially containing tables
    if (parsedContent.nodeType === 'table') {
        return renderSingleTable(parsedContent, className, tableType || (content && content.type));
    }

    // If it's a document with potential tables, render the mixed content
    return renderRichContent(parsedContent, className, tableType || (content && content.type));
};

/**
 * Renders rich content that may contain tables and text
 */
const renderRichContent = (contentNode, className, tableType) => {
    if (!contentNode || !contentNode.content) return null;
    
    const renderContentBlocks = (node) => {
        if (!node || !node.content) return null;
        
        return node.content.map((childNode, index) => {
            // Handle table nodes
            if (childNode.nodeType === 'table') {
                return renderSingleTable(childNode, className, tableType);
            }
            
            // Handle text/paragraph nodes
            if (childNode.nodeType === 'paragraph' || childNode.nodeType === 'text' || 
                childNode.nodeType === 'document' || childNode.nodeType.startsWith('heading-')) {
                // Check if the content is empty before rendering
                if (childNode.nodeType === 'text' && (!childNode.value || childNode.value.trim() === '')) {
                    return null;
                }
                
                // For paragraph nodes, check if they have any non-empty content
                if (childNode.nodeType === 'paragraph' && childNode.content) {
                    const hasNonEmptyContent = childNode.content.some(item => 
                        !(item.nodeType === 'text' && (!item.value || item.value.trim() === ''))
                    );
                    if (!hasNonEmptyContent) {
                        return null;
                    }
                }
                
                return <Text 
                    key={`text-${index}`} 
                    content={childNode}
                />;
            }
            
            // Recursively handle embedded content
            if (childNode.content) {
                return <React.Fragment key={`fragment-${index}`}>{renderContentBlocks(childNode)}</React.Fragment>;
            }
            
            return null;
        });
    };

    let wrapperStyle;    
    if (tableType === 'Dosing') {
        wrapperStyle = {TableWrapperDosing};
    } else {
        wrapperStyle = {TableWrapperDefault};
    }

    // Conditional return based on type
    if (tableType === 'Pain' || tableType === 'Dosing' || tableType === 'Safety' || tableType === 'MultiModal Protocol' || tableType === 'MultiModal Protocol 2' || tableType === 'Pediatric Surgery') {
            return <div className="relative w-full overflow-x-auto overflow-y-hidden">
                    <ScrollableTableContainer className={`${tableType === 'Dosing' ? 'rounded-all_32' : ""}`}>
                        <ScrollableTables className={`${tableType === 'Dosing' ? wrapperStyle.TableWrapperDosing : wrapperStyle.TableWrapperDefault}`}>
                        {renderContentBlocks(contentNode)}
                        </ScrollableTables>
                    </ScrollableTableContainer>
                    <div className="flex justify-center items-center gap-2 md:hidden">
                        <img src={InfoIcon} alt="Swipe left" />
                        <p className={`text-p_footnote font-bold ${tableType === 'MultiModal Protocol' || tableType === 'MultiModal Protocol 2' ? 'text-white' : "text-dark_green"}`}>Swipe left on table to see more</p>
                    </div>
                </div>;
            } else if (tableType === 'Access Doses' || tableType === 'Reimbursement Calculation') {
                return <>
                    {renderContentBlocks(contentNode)}
                </>;
            } else {
                return <div>
                    {renderContentBlocks(contentNode)}
                </div>;
        }
};

/**
 * Renders a single table node
 */
const renderSingleTable = (tableNode, className, type) => {
    if (!tableNode || !tableNode.content) {
        console.error("Invalid table node");
        return null;
    }

    const rows = tableNode.content;

    if (!rows || rows.length === 0) {
        console.error("No rows found in table");
        return null;
    }

    // Find if first row contains header cells
    const hasHeaders = rows[0]?.content?.some?.(cell => cell.nodeType === 'table-header-cell');

    // Recursive function to render content with formatting
    const renderFormattedContent = (node) => {
        if (!node) return null;

        // If it's a text node with formatting
        if (node.nodeType === 'text') {
            if (!node.marks || node.marks.length === 0) {
                // Handle text with bullet points (•)
                if (node.value && node.value.includes('•')) {
                    // Split by bullet points and create list items
                    const segments = node.value.split('•').filter(segment => segment.trim() !== '');
                    if (segments.length > 0) {
                        return (
                            <ul className="list-disc pl-6">
                                {segments.map((segment, i) => (
                                    <li key={i}>{segment.trim()}</li>
                                ))}
                            </ul>
                        );
                    }
                }
                
                // Handle text with line breaks
                if (node.value && node.value.includes('\n')) {
                    // Split by newline and render with <br/> elements between segments
                    return node.value.split('\n').map((segment, i, arr) => (
                        <React.Fragment key={i}>
                            {segment}
                            {i < arr.length - 1 && <br/>}
                        </React.Fragment>
                    ));
                }
                return node.value || <br/>;
            }

            // Apply formatting based on marks
            let formattedText = node.value || '';
            const marks = node.marks || [];

            // Apply marks (formatting) to the text
            marks.forEach(mark => {
                switch (mark.type) {
                    case 'paragraph':
                        formattedText = <p key={`paragraph-${formattedText}`}>{formattedText}</p>;
                        break;
                    case 'bold':
                        formattedText = <strong key={`bold-${formattedText}`}>{formattedText}</strong>;
                        break;
                    case 'italic':
                        formattedText = <em key={`italic-${formattedText}`}>{formattedText}</em>;
                        break;
                    case 'underline':
                        formattedText = <u key={`underline-${formattedText}`}>{formattedText}</u>;
                        break;
                    case 'superscript':
                        formattedText = <sup key={`superscript-${formattedText}`}>{formattedText}</sup>;
                        break;
                        
                    default:
                        // Other mark types can be handled here
                        break;
                }
            });

            return formattedText;
        }

        // For nodes with children
        if (node.content) {
            const children = node.content.map((child, index) =>
                <React.Fragment key={index}>{renderFormattedContent(child)}</React.Fragment>
            );

            // If it's a paragraph, wrap it in a paragraph tag
            if (node.nodeType === 'paragraph') {
                return <React.Fragment>{children}</React.Fragment>;
            }

            return children;
        }

        return null;
    };

    const getTableWithType = (tableContent) => {
        switch (type) {
            case 'Pain':
                return <PainTable className={className}>{tableContent}</PainTable>;
            case 'Safety':
                return <SafetyTable className={className}>{tableContent}</SafetyTable>;
            case 'Dosing':
                return <DosingTable className={className}>{tableContent}</DosingTable>;
            case 'Access Doses':
            return <AccessDosesTable className={className}>{tableContent}</AccessDosesTable>;
            case 'Reimbursement Calculation':
                return <AccessReimbursementTable className={className}>{tableContent}</AccessReimbursementTable>;
            case 'MultiModal Protocol':
                return <MultiModalProtocolTable className={className}>{tableContent}</MultiModalProtocolTable>;
            case 'MultiModal Protocol 2':
                return <MultiModalProtocolTable2 className={className}>{tableContent}</MultiModalProtocolTable2>;
            case 'Pediatric Surgery':
                return <PediatricSurgeryTable className={className}>{tableContent}</PediatricSurgeryTable>;
            case 'other':
                return <otherTable className={className}>{tableContent}</otherTable>;
            default:
                return <table className={className}>{tableContent}</table>;
        }
    };

    const tableContent = (
        <>
            {hasHeaders && (
                <thead>
                    <tr>
                        {rows[0].content.map((cell, cellIndex) => (
                            <th key={cellIndex}>
                                {renderFormattedContent(cell)}
                            </th>
                        ))}
                    </tr>
                </thead>
            )}
            <tbody>
                {rows.slice(hasHeaders ? 1 : 0).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {row.content.map((cell, cellIndex) => (
                            <td key={cellIndex}>
                                {renderFormattedContent(cell)}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </>
    );

    return getTableWithType(tableContent);
};

// Add PropTypes for better documentation and type checking
Table.propTypes = {
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    className: PropTypes.string,
    tableType: PropTypes.string
};

export default Table;
